# THE TEACHER PORTAL'S ROUTES (/teacher) — card 6ac022cba664bc34d57be8c3.
#
# Same shape as the parent shell's (../routes.coffee; router/hash_router says
# what an entry may carry), and the same URLs the portal has always had —
# including the mailed reset link <base>/teacher#/reset?token= and #/onboarding.
import { screen } from '../shell.coffee'
import { redirect } from '../router/hash_router.coffee'
import {studentRedirect} from '../student_route_guard.coffee'
import {servesBootRead} from '../dead_session.coffee'

Assignment         = screen (-> import('../Assignment/assignment.coffee')), 'assignment'
Cancel             = screen (-> import('../CancellationSurvey/new_cancellation_survey.coffee')), 'new_cancellation_survey'
ClassroomFund      = screen (-> import('./classroom_fund.coffee')), 'classroom_fund'
Help               = screen (-> import('../Help/help.coffee')), 'help'
# Institutional sign-in, NOT the parent screen. The parent flow emails a
# generated password on request, which is designed behaviour for a parent on a
# child's device and the wrong thing entirely for a login holding roster and
# student data. Card 6a639e22893d0bbbaaacec81.
Login              = screen (-> import('../Login/staff_login.coffee')), 'staff_login'
ResetPassword      = screen (-> import('../Login/reset_password.coffee')), 'reset_password'
Dashboard          = screen (-> import('../Dashboard/dashboard.coffee')), 'dashboard'
LongMilestoneReport = screen (-> import('../MilestoneReport/long_milestone_report.coffee')), 'long_milestone_report'
ManageAccount      = screen (-> import('../ManageAccount/account.coffee')), 'account'
TeacherDashboard   = screen (-> import('./teacher.coffee')), 'teacher'
TeacherOnboarding  = screen (-> import('./onboarding.coffee')), 'onboarding'
UpdateScholarship  = screen (-> import('../UpdateScholarship/update_scholarship.coffee')), 'update_scholarship'
GameLayout         = screen (-> import('../Game/game_layout.coffee')), 'game_layout'
StudentApplication = screen (-> import('../StudentApplication/application.coffee')), 'application'
StudentReport      = screen (-> import('../StudentReport/student_report.coffee')), 'student_report'
GamesList          = screen (-> import('../GameList/games.coffee')), 'games'
SubjectList        = screen (-> import('../SubjectList/subjects.coffee')), 'subjects'
Tokens             = screen (-> import('../Tokens/tokens.coffee')), 'tokens'

requireLogin = (target) ->
  unless window.app?.loopbackAPI?.isLoggedIn()
    console.log "Redirecting the user to the login page..."
    return {state: 'login'}

# The four student-scoped screens, guarded — card 6aaedd51a822f303b055f830.
# The rule is in student_route_guard.coffee; what belongs to this shell is its
# sign-in path and the name of the screen a teacher goes home to.
requireStudent = (target) ->
  return requireLogin(target) unless window.app?.loopbackAPI?.isLoggedIn()
  studentRedirect target, 'teacherDashboard'

export guards = {requireLogin, requireStudent}

refreshIfNeeded = ->
  if window.app.needsRefresh() then return window.app.refresh() else return null

export routes =
  # Deliberately NOT behind requireLogin — someone redeeming a reset link
  # cannot sign in, which is the entire point. Signed-in users reach the same
  # screen from Manage Account, where it asks for the current password.
  '/reset?token':
    name: 'reset'
    component: ResetPassword
    resolve:
      none: -> window.app.trackPageView("2: Reset Password")

  '':
    name: 'login'
    component: Login
    resolve:
      none: -> window.app.trackPageView("2: Login")

  '/application':
    name: 'application'
    requireLogin: true
    component: StudentApplication
    resolve:
      none: ->
        window.app.trackPageView("2: Application")
        if fbq?
          fbq('track', 'Lead')

  '/account?tab':
    name: 'account'
    requireLogin: true
    component: ManageAccount
    resolve:
      none: ->
        window.app.trackPageView("2: Account")
        refreshIfNeeded()

  '/classroom_fund':
    name: 'classroom_fund'
    requireLogin: true
    component: ClassroomFund
    resolve:
      none: -> window.app.trackPageView("2: Classroom Fund")

  '/help':
    name: 'help'
    params:
      url: {value: null}
    requireLogin: true
    component: Help
    resolve:
      initialized: -> window.app.trackPageView("2: Help")

  '/update_scholarship?back':
    name: 'update_scholarship'
    requireLogin: true
    component: UpdateScholarship
    resolve:
      initialized: -> window.app.trackPageView("2: Update Scholarship")

  '/teacher_dashboard?showIntro&tab':
    name: 'teacherDashboard'
    # dynamic: the tab bar writes the selected tab here (card
    # 6a8ca1e49b201267b64647bf) without remounting itself. A PLAIN param
    # re-enters the route on every change, which destroys and rebuilds
    # TeacherDashboard — and its constructor starts at `activeFolder: 'All'`,
    # so picking a folder and then clicking a tab silently dropped the teacher
    # back to All Students while the tab itself survived through its params.
    # Same shape, and same reason, as `studentId` on `dashboard` below (card
    # 6aa8b5d750645cd1c3e765ab).
    dynamic: ['tab']
    requireLogin: true
    component: TeacherDashboard
    resolve:
      none: ->
        window.app.activeStudentIndex = null
        window.app.trackPageView("2: Teacher Dashboard")
        refreshIfNeeded()

  '/onboarding':
    name: 'teacher_onboarding'
    requireLogin: true
    component: TeacherOnboarding
    resolve:
      none: -> window.app.trackPageView("2: Teacher Onboarding")

  '/long_milestone_report?studentId':
    name: 'long_milestone_report'
    requireStudent: true
    component: LongMilestoneReport
    resolve:
      initialized: ->
        window.app.trackPageView("2: Long Placement Exam Report")
        refreshIfNeeded()

  '/assignment':
    name: 'assignment'
    requireLogin: true
    component: Assignment
    resolve:
      assignments: ->
        window.app.trackPageView("2: Assignment")
        window.app.loopbackAPI.Assignment.find({})

  '/dashboard?studentId':
    name: 'dashboard'
    # dynamic: the dashboard writes the selected child here as the teacher swipes
    # (card 6aa8b5d750645cd1c3e765ab) without remounting itself.
    dynamic: ['studentId']
    requireLogin: true
    component: Dashboard
    resolve:
      initialized: ->
        window.app.trackPageView("2: Dashboard")
        refreshIfNeeded()

  '/games?studentId':
    name: 'games'
    requireStudent: true
    component: GamesList
    resolve:
      games: ({studentId}) ->
        window.app.refresh() if window.app.needsRefresh()
        window.app.trackPageView("2: Game List")
        # studentId comes from the route's params, NOT from the active student:
        # this resolve runs BEFORE digestParams latches it, so reading the
        # active student here filters the game voices for the child being
        # navigated away from (card 6aa4313530706c48305d2195).
        window.app.loadGames(studentId)

  '/subjects?studentId':
    name: 'subjects'
    requireStudent: true
    component: SubjectList
    resolve:
      subjects: -> window.app.trackPageView("2: Subject List")
      refresh: ->
        if window.app.needsRefresh()
          student = window.app.getActiveStudent()
          # `student?` — the active student is not guaranteed here. This resolve
          # runs whenever needsRefresh() says yes, and since card
          # 6aade038bf4219e77902d92f that includes an Application that has never
          # loaded an account, where there is no active student to preserve a
          # session_duration from. The refresh below is exactly what fills it in.
          session_duration = if student?.session_duration? then student.session_duration else null
          p = window.app.refresh().then ->
            window.app.getActiveStudent()?.session_duration = session_duration if session_duration?
          return p

  '/student_report?studentId':
    name: 'student_report'
    # A whole folder at once, from teacher.coffee: not in the URL.
    params:
      studentIds: {value: null, array: true}
    requireStudent: true
    component: StudentReport
    resolve:
      initialized: ->
        window.app.trackPageView("2: Student Report")
        refreshIfNeeded()

  '/cancel':
    name: 'cancel'
    requireLogin: true
    component: Cancel
    resolve:
      initialized: -> window.app.trackPageView("2: Cancel")

  '/play/:gameId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete':
    name: 'play'
    requireLogin: true
    component: GameLayout
    resolve:
      games: ({studentId}) ->
        window.app.trackPageView("2: Game Play")
        # studentId comes from the route's params, NOT from the active student
        # (card 6aa4313530706c48305d2195).
        window.app.loadGames(studentId)

  '/playsubject/:subjectId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete':
    name: 'playSubject'
    requireLogin: true
    component: GameLayout
    resolve:
      studentRefresh: ({studentId}) ->
        console.log "Refreshing student #{studentId}..."
        window.app.loopbackAPI.Student.findById({id: studentId}, {include: {relation: 'posetProfiles', scope: {include: {relation: 'poset', scope: {fields: ['name']}}}}}).then (response) ->
          student = window.app.getStudentById(studentId)
          if student
            student.posetProfiles = response.data.posetProfiles
            window.app.dirty = true
      games: ({studentId}) ->
        window.app.trackPageView("2: Game Play")
        # studentId comes from the route's params, NOT from the active student
        # (card 6aa4313530706c48305d2195).
        window.app.loadGames(studentId)

  '/tokens':
    name: 'tokens'
    requireLogin: true
    component: Tokens
    resolve:
      initialized: ->
        window.app.trackPageView("2: Login Tokens")
        # REFRESH ON THE WAY IN, UNCONDITIONALLY — card 6ab167dba4d698cda2b1fa9c.
        # This was the one route whose resolve did not refresh at all, so the QR
        # sheet rendered whatever window.app.students happened to hold: a tab
        # open since before a child was archived (on another device, or in the
        # other shell) kept offering that child a working login.
        #
        # `if window.app.needsRefresh()` — what every other route asks — is NOT
        # enough here, and measuring it is the only way to see that: needsRefresh
        # answers true only for a tab that made the change itself (window.app.dirty)
        # or one older than thirty minutes. A child archived on ANOTHER device
        # sets neither, so the guarded form left this screen listing the archived
        # child exactly as before (measured on ripperws2 2026-09-21).
        #
        # So this screen always re-reads. It is entered deliberately and rarely,
        # it costs one getAccount, and what it renders is PRINTED and handed to a
        # classroom — a stale list here is a child signing in after the teacher
        # believes they cannot.
        #
        # GUARDED, for the same reason the boot read is — card
        # 6ab69fbe027287c485001e30. A signed-in session whose manifest does not
        # serve `Account.getAccount` would throw here, synchronously; the
        # requireLogin guard only asks whether a token is stored. So this sends
        # the session to sign in, by answering a redirect rather than by
        # touching the hash mid-transition.
        unless servesBootRead window.app?.loopbackAPI
          console.warn "This session's API does not serve Account.getAccount — sending this session to sign in"
          return redirect 'login'
        return window.app.refresh()
